import { VaultCodeEnum, getVaultObjectCode } from "./type/dali.type";
import { StoreModel } from "./vault-parser/parser.type";
import { ParserFactory } from "./vault-parser/parser.factory";
import { VaultItemType } from "./vault.reader";
import { CipherView } from "@bitwarden/common/src/models/view/cipher.view";

/** An item parseVaultData skipped. `id` is the only item data kept, so it's safe to log. **/
export type VaultItemParseFailure = { id: string | undefined; error: unknown };

export class VaultModelUpgrader {
  private static instance: VaultModelUpgrader;
  private readonly parserFactory: ParserFactory = new ParserFactory();

  // Private constructor to prevent direct instantiation
  private constructor() {}

  /**
   * Get the shared singleton instance of VaultAdapter.
   */
  static getInstance(): VaultModelUpgrader {
    if (!this.instance) {
      this.instance = new VaultModelUpgrader();
    }
    return this.instance;
  }

  /**
   * Parses one cipher's items into `storeData`. An item that fails to parse is skipped and
   * returned, rather than aborting the rest of the cipher: one bad row mustn't hide its siblings
   * or, with a shared `storeData`, leave the cipher half-loaded. The writer never rewrites items
   * it wasn't asked to save, so a skipped item stays on disk untouched.
   */
  parseVaultData(
    storeData: Map<VaultCodeEnum, StoreModel[]>,
    collections: VaultItemType[],
    cipher: CipherView,
  ): VaultItemParseFailure[] {
    const vaultCode = getVaultObjectCode(cipher.name);
    const failures: VaultItemParseFailure[] = [];

    /**
     * Notes that parse to a JSON string would otherwise be iterated character by character. A
     * fixed message, like the item check below, keeps the notes' contents out of the logs.
     **/
    if (!Array.isArray(collections)) {
      throw new TypeError("Vault notes are not an array");
    }

    for (const vaultItem of collections) {
      /**
       * The whole body sits in the try, push last, so an item that isn't an object (corrupt or
       * externally edited notes) is skipped like any other bad item instead of aborting the rest
       * of the cipher. It's rejected up front: assigning `vid` doesn't throw on an array, and on a
       * string the engine's TypeError quotes the item's contents.
       **/
      try {
        if (typeof vaultItem !== "object" || vaultItem === null || Array.isArray(vaultItem)) {
          throw new TypeError("Vault item is not an object");
        }
        /** Attach vault id to the element **/
        vaultItem["vid"] = cipher.id;
        const parser = this.parserFactory.getParserFunction(vaultCode, this.versionOf(vaultItem));
        const parsedItem = parser(vaultItem);
        /**
         * oid is never saved to the cipher itself - it's just a reference to the cipher's own
         * organizationId, so it must be re-derived from `cipher` on every load rather than trusted
         * to survive parsing. Assigned after the parser runs because version-upgrade DTOs rebuild a
         * whitelisted object and don't carry it through the transform chain.
         **/
        parsedItem["oid"] = cipher.organizationId ?? undefined;
        if (storeData.has(vaultCode)) {
          storeData.get(vaultCode).push(parsedItem);
        } else {
          storeData.set(vaultCode, [parsedItem]);
        }
      } catch (error) {
        /** `||` matches the writer's `m.id ? … : m._id`, so a row with `id: ""` is named by _id **/
        failures.push({ id: vaultItem?.id || vaultItem?._id, error });
      }
    }

    return failures;
  }

  /**
   * A parser version identifies the version the item *currently is*, so it must come from the
   * item being transformed rather than a caller-supplied target. An absent `v` means the item
   * predates versioning, i.e. v0. A non-numeric `v` falls back to v0 too, rather than propagating
   * NaN into isSupportedVersion (which would throw "version NaN" instead of resolving to the
   * documented legacy fallback). `parseInt` stringifies its argument and only ever returns an
   * integer or NaN, so `|| 0` covers missing, null and non-numeric values alike.
   */
  private versionOf(vaultItem: VaultItemType): number {
    return parseInt(vaultItem.v, 10) || 0;
  }
}
